/**
 * Battle modes (Phase 5). Pure config + helpers so the values are testable and shared
 * between the menu, the battle scene, and the reward math — no Phaser dependency.
 *
 * - normal:  the default 1v1 skirmish.
 * - hard:    same rules, but the AI starts under heavier pressure (tougher enemies) and
 *            pays out more War Funds.
 * - endless: the win condition is suppressed — you can't "finish" by cracking the AI
 *            fortress; you survive as long as possible against escalating pressure, and
 *            score on survival time. Big War Funds payout.
 * - daily:   the seeded Daily Challenge. Win-based like normal/hard, but the day's modifiers
 *            (rolled deterministically from the date) ride the pressure/gold hooks. Not part
 *            of the menu's 3-way selector — it has its own entry point.
 */
import { DAILY_BASE_PRESSURE } from "./dailyChallenge";

export type BattleMode = "normal" | "hard" | "endless" | "daily";

/** Modes shown in the menu's mode selector (daily has its own button, so it is excluded). */
export const BATTLE_MODES: BattleMode[] = ["normal", "hard", "endless"];

export function isBattleMode(value: unknown): value is BattleMode {
  return value === "normal" || value === "hard" || value === "endless" || value === "daily";
}

/** Starting DifficultySystem pressure for a mode (clamped 0.55–1.75 by the system). */
export function modeStartPressure(mode: BattleMode): number {
  switch (mode) {
    case "hard":
      return 1.35;
    case "endless":
      return 1.15;
    case "daily":
      return DAILY_BASE_PRESSURE;
    case "normal":
    default:
      return 1;
  }
}

/** War Funds payout multiplier for a mode (reward for taking on more difficulty). */
export function modeRewardMultiplier(mode: BattleMode): number {
  switch (mode) {
    case "hard":
      return 1.5;
    case "endless":
      return 1.75;
    case "daily":
      return 1.6;
    case "normal":
    default:
      return 1;
  }
}

/** Whether the standard "counterattack reached the AI fortress" win should fire. */
export function modeAllowsWin(mode: BattleMode): boolean {
  return mode !== "endless";
}
